// ── MOZI print template (Manufacturing Order, Finish Sheets) ──────────────────
// Same family as the QC report: warm paper, brand header (no furniture photo),
// brown tab headings, item table with
// reference images, sign-off boxes and a branded footer. Documents pass in only
// saved data; missing values print as blank lines. Paginated by paged.js (one
// print job, repeating table header, page numbers).
import { buildImageUrl } from '../../utils/imageUrl';

export const T = {
  ink: '#2b2a27', logo: '#4a4845', walnut: '#9a5c2e', bronze: '#a8743f', gold: '#b08a4f',
  tan: '#ece3d6', muted: '#7a746c', line: '#e2dbd1', soft: '#f6f1ea', paper: '#fbf8f3', charcoal: '#3a3936',
};
export const BRAND_TAGLINE = 'Furniture | Spaces | Solutions';
export const FOOT_TAGLINE = 'Quality Manufacturing  |  Precision  |  Timely Delivery';

export const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
export const cssStr = v => String(v ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/</g, '\\3C ').replace(/[\r\n]+/g, ' ');
export const has = v => v != null && String(v).trim() !== '';
export const fmtDate = v => {
  if (!has(v)) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
export const fmtNum = v => { const n = Number(v); return Number.isFinite(n) ? (Number.isInteger(n) ? String(n) : String(+n.toFixed(2))) : ''; };
export const val = (v, cls = '') => has(v) ? `<div class="v${cls}">${esc(v)}</div>` : '<div class="blank"></div>';

// Small line icons for the header strip and tab headings (inline SVG prints crisply)
export const ICON = {
  gear: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3.2"/><path d="M12 2.8v2.6M12 18.6v2.6M4.2 7.5l2.2 1.3M17.6 15.2l2.2 1.3M4.2 16.5l2.2-1.3M17.6 8.8l2.2-1.3"/><circle cx="12" cy="12" r="7"/></svg>',
  layers: '<svg viewBox="0 0 24 24"><path d="M12 3l9 5-9 5-9-5 9-5z"/><path d="M3 12.5l9 5 9-5"/><path d="M3 16.5l9 5 9-5"/></svg>',
  check: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M7.8 12.3l2.9 2.9 5.6-6"/></svg>',
  doc: '<svg viewBox="0 0 24 24"><path d="M6 3h8l4 4v14H6z"/><path d="M14 3v4h4"/><path d="M9 12h6M9 15.5h6M9 8.5h2.5"/></svg>',
  box: '<svg viewBox="0 0 24 24"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M4 7.5l8 4.5 8-4.5M12 12v9"/></svg>',
};


// Brand name for the header: first word of the firm name (e.g. "MOZI")
export const brandWordOf = settings => (((settings && settings.company_name) || '').trim().split(/\s+/)[0] || '').toUpperCase();

// Reference image cell: first photo, "+N" when there are more
export function thumbCell(images, emptyText = 'No image', small = false) {
  const list = images || [];
  const cls = small ? 'thumb sm' : 'thumb';
  if (!list.length) return `<div class="${cls} empty">${esc(emptyText)}</div>`;
  return `<div class="${cls}"><img src="${esc(buildImageUrl(list[0].image_path))}" alt="" />${list.length > 1 ? `<span class="more">+${list.length - 1}</span>` : ''}</div>`;
}

// Page-1 header: brand, two-line title (second line in bronze), sub line, feature strip
export function heroHtml({ brandWord, titleTop, titleAccent, sub }) {
  return `
  <header class="hero">
    <div class="corner-tag">Better<br/>Furniture<br/>Better<br/>Spaces<span></span></div>
    ${brandWord ? `<div class="wm">${esc(brandWord)}</div><div class="tag">${esc(BRAND_TAGLINE)}</div><div class="rule"></div>` : ''}
    <h1 class="title">${esc(titleTop)}<br/><b>${esc(titleAccent)}</b></h1>
    ${has(sub) ? `<div class="sub">${esc(sub)}</div>` : ''}
    <div class="feats">
      <div class="ft">${ICON.gear}<span>Precision<br/>Manufacturing</span></div>
      <div class="ft">${ICON.layers}<span>Quality<br/>Craftsmanship</span></div>
      <div class="ft">${ICON.check}<span>On Time<br/>Delivery</span></div>
    </div>
  </header>`;
}

// Brown slanted tab heading; width = room for the title text
export const tabHtml = (icon, title, width) => `<h2 class="tab" style="--w:${width}">${ICON[icon] || ''}<span>${title}</span></h2>`;

// Details card: [[label, value, ' big'?], ...] in rows of four
export const detailsHtml = details => `<div class="details">${details.map(([k, v, cls]) =>
  `<div class="d"><div class="k">${esc(k)}</div>${val(v, cls || '')}</div>`).join('')}</div>`;

// Sign-off boxes + remarks box
export const signoffHtml = (signs, remarks) => `
  <div class="signoff">
    ${signs.map(([k, v]) => `<div class="sg"><div class="sk">${esc(k)}</div><div class="sv">${has(v) ? esc(v) : ''}</div><div class="sl">Name / Sign</div></div>`).join('')}
    <div class="sg rmk"><div class="sk">Remarks</div><div class="rtext">${esc(remarks || '')}</div></div>
  </div>`;

// Full printable document around a body. Adds the running footer element.
export function buildMozDocument({ docTitle, body, settings = {}, origin = window.location.origin, landscape = false, extraCss = '' }) {
  const brandWord = brandWordOf(settings);
  body = `<div class="runFoot">${brandWord ? `<span class="rf-word">${esc(brandWord)}</span>` : ''}<span class="rf-tag">${esc(BRAND_TAGLINE)}</span></div>` + body;
  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>${esc(docTitle)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Montserrat:wght@500;600;700;800&display=swap" rel="stylesheet">
  <script>window.PagedConfig = { auto: false };<\/script>
  <script src="https://unpkg.com/pagedjs@0.4.3/dist/paged.polyfill.js"><\/script>
  <style>
    @page {
      size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 14mm 12mm 18mm 12mm;
      @bottom-left { content: element(runFoot); vertical-align: middle; }
      @bottom-right { content: "${cssStr(FOOT_TAGLINE.toUpperCase())}\u2003\u2003" counter(page, decimal-leading-zero) " / " counter(pages, decimal-leading-zero);
        font: 600 6.2pt Montserrat, Inter, Arial, sans-serif; letter-spacing: .14em; color: ${T.muted}; vertical-align: middle; }
    }
    /* Page 1 starts with the MOZI header: only a small top margin there */
    @page :first { margin-top: 6mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Inter, Arial, sans-serif; color: ${T.ink}; font-size: 8.6pt; line-height: 1.35; }
    svg { fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }

    /* Page decoration: warm paper + soft shapes on every page, charcoal corner on page 1 */
    .pagedjs_pagebox { position: relative; z-index: 0; background: ${T.paper}; }
    .pagedjs_pagebox::before { content: ""; position: absolute; inset: 0; z-index: -1; pointer-events: none;
      background: linear-gradient(118deg, transparent 0 60%, rgba(176,138,79,.07) 60% 72%, transparent 72%),
                  linear-gradient(30deg, rgba(176,138,79,.08) 0 13%, transparent 13%); }
    .runFoot { position: running(runFoot); display: flex; align-items: center; gap: 4mm; }
    .rf-word { font: 800 13pt/1 Montserrat, Inter, sans-serif; color: ${T.logo}; margin-right: 4mm; }
    .rf-tag { font: 600 5.4pt Montserrat, Inter, sans-serif; letter-spacing: .28em; text-transform: uppercase; color: ${T.muted}; }

    /* Hero header (page 1) */
    .hero { position: relative; padding-bottom: 1mm; }
    .corner-tag { position: absolute; top: -6mm; right: -12mm; width: 34mm; height: 38mm; background: ${T.charcoal};
      clip-path: polygon(0 0, 100% 0, 100% 100%); color: #e9e4dc; text-align: right; padding: 5mm 5mm 0 0;
      font: 600 5.4pt/1.7 Montserrat, Inter, sans-serif; letter-spacing: .3em; text-transform: uppercase; }
    .corner-tag span { display: block; width: 8mm; height: .5mm; background: ${T.gold}; margin: 2mm 0 0 auto; }
    .wm { font: 800 24pt/1 Montserrat, Inter, sans-serif; letter-spacing: -.01em; color: ${T.logo}; padding-top: 0; }
    .tag { font: 600 5.8pt Montserrat, Inter, sans-serif; letter-spacing: .32em; text-transform: uppercase; color: ${T.muted}; margin-top: 1.2mm; }
    .rule { width: 14mm; height: .7mm; background: ${T.bronze}; margin: 2.5mm 0 3mm; }
    .title { font: 800 19pt/1.05 Montserrat, Inter, sans-serif; text-transform: uppercase; color: ${T.ink}; letter-spacing: .01em; }
    .title b { color: ${T.bronze}; font-weight: 800; }
    .sub { font: 600 6.4pt Montserrat, Inter, sans-serif; letter-spacing: .24em; text-transform: uppercase; color: ${T.muted}; margin-top: 1.2mm; }
    .feats { display: flex; gap: 0; margin-top: 3mm; width: 112mm; }
    .ft { display: flex; align-items: center; gap: 2.5mm; padding: 0 5mm; border-left: .3mm solid ${T.line}; }
    .ft:first-child { padding-left: 0; border-left: 0; }
    .ft svg { width: 6.5mm; height: 6.5mm; color: ${T.ink}; flex: 0 0 auto; }
    .ft span { font: 600 5.8pt/1.5 Montserrat, Inter, sans-serif; letter-spacing: .22em; text-transform: uppercase; color: ${T.ink}; }

    /* Tab headings: brown slanted tab on a tan band */
    .tab { position: relative; display: flex; align-items: center; gap: 3mm; margin-top: 4mm; background: ${T.tan}; break-after: avoid; }
    .tab::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: var(--w, 72mm);
      background: linear-gradient(90deg, ${T.walnut}, ${T.bronze}); clip-path: polygon(0 0, calc(100% - 6mm) 0, 100% 100%, 0 100%); }
    .tab svg { position: relative; width: 6mm; height: 6mm; color: #fff; margin-left: 3mm; flex: 0 0 auto; }
    .tab span { position: relative; padding: 2mm 0; color: #fff; font: 700 8.6pt Montserrat, Inter, sans-serif; letter-spacing: .12em; text-transform: uppercase; }

    /* Order details card */
    .details { display: grid; grid-template-columns: 1.15fr 0.9fr 1.35fr 0.95fr; background: #fff; border: .3mm solid ${T.line}; border-top: 0; }
    .d { padding: 2.4mm 3mm; border-right: .3mm solid ${T.line}; border-bottom: .3mm solid ${T.line}; min-height: 14mm; }
    .d:nth-child(4n) { border-right: 0; } .d:nth-last-child(-n+4) { border-bottom: 0; }
    .k { font-size: 7.2pt; color: ${T.muted}; margin-bottom: 1.2mm; }
    .v { font-weight: 700; font-size: 8.8pt; color: ${T.ink}; word-break: break-word; }
    .v.big { font: 800 11pt Montserrat, Inter, sans-serif; letter-spacing: .02em; }
    .blank { width: 30mm; max-width: 100%; border-bottom: .3mm solid #b3aa9e; height: 4mm; }

    /* Product table */
    table.items { width: 100%; border-collapse: collapse; background: #fff; border: .3mm solid ${T.line}; border-top: 0; }
    .items thead { display: table-header-group; }
    .items th { background: ${T.soft}; font: 700 7pt Montserrat, Inter, sans-serif; letter-spacing: .02em; color: ${T.ink}; text-align: left;
      padding: 2.2mm 2mm; border: .3mm solid ${T.line}; vertical-align: middle; }
    .items td { border: .3mm solid ${T.line}; padding: 1.5mm 2mm; vertical-align: middle; font-size: 8pt; }
    .items tr { break-inside: avoid; }
    table.items[data-split-from] { margin-top: 10mm; }          /* room for the repeated header */
    table.items[data-split-from].has-head { margin-top: 0; }
    .items .c { text-align: center; }
    .items .sn { width: 9mm; color: ${T.muted}; }
    .items .pc { width: 25mm; font-weight: 600; font-size: 7.6pt; overflow-wrap: anywhere; }
    .items .pname { font-weight: 700; font-size: 8.6pt; }
    .items .psub { font-size: 7.4pt; color: #5d5850; margin-top: .7mm; }
    .items .im { width: 30mm; text-align: center; }
    .thumb { position: relative; width: 25mm; height: 19mm; margin: 0 auto; background: ${T.soft}; display: flex; align-items: center; justify-content: center; overflow: hidden; }
    .thumb img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
    .thumb.empty { font-size: 6.8pt; color: ${T.muted}; }
    .thumb .more { position: absolute; right: .8mm; bottom: .8mm; background: rgba(43,42,39,.78); color: #fff; font-size: 6.4pt; font-weight: 700; padding: .2mm 1.2mm; border-radius: 2mm; }
    .items .fn { width: 30mm; }
    .chips { display: flex; flex-wrap: wrap; gap: 1mm; margin-bottom: 1mm; }
    .chip { display: inline-flex; align-items: center; gap: 1mm; font-size: 6.6pt; font-weight: 700; color: ${T.walnut}; background: ${T.tan}; padding: .4mm 1.6mm; border-radius: 2mm; }
    .fnote { font-size: 7.2pt; color: #4a4640; }
    .items .qty { width: 11mm; font-weight: 800; font-size: 9pt; }
    .items .rm { width: 27mm; font-size: 7.4pt; color: #4a4640; }
    .items .none { text-align: center; color: ${T.muted}; padding: 6mm; }
    .items tfoot td { background: ${T.soft}; font-weight: 700; }
    .items .tl { text-align: right; font-size: 7.6pt; color: ${T.muted}; }

    /* Sign-off */
    .signoff { display: grid; grid-template-columns: 1fr 1fr 1fr 1.3fr; margin-top: 5mm; background: #fff; border: .3mm solid ${T.line}; break-inside: avoid; }
    .sg { padding: 2.2mm 3mm; border-right: .3mm solid ${T.line}; min-height: 20mm; display: flex; flex-direction: column; }
    .sg:last-child { border-right: 0; }
    .sk { font-size: 7.2pt; color: ${T.muted}; }
    .sv { flex: 1; display: flex; align-items: flex-end; font-weight: 700; font-size: 8.4pt; padding-bottom: 1mm; border-bottom: .3mm solid #b3aa9e; margin-top: 3mm; }
    .sl { font-size: 6.8pt; color: ${T.muted}; margin-top: 1mm; }
    .rtext { margin-top: 1.5mm; font-size: 7.6pt; white-space: pre-wrap; color: #3d3a35; }

    @media screen { body { background: #e7e2da; } .pagedjs_page { margin: 0 auto 8mm; box-shadow: 0 2px 10px rgba(0,0,0,.15); } }
    /* Landscape documents: compact page-1 header so the table starts higher */
    body.landscape .wm { font-size: 22pt; }
    body.landscape .title { font-size: 17pt; }
    body.landscape .feats { position: absolute; left: 112mm; top: 12mm; margin-top: 0; }
    /* Small reference images for wide tables */
    .thumb.sm { width: 22mm; height: 17mm; }
    ${extraCss}
  </style></head><body${landscape ? ' class="landscape"' : ''}>
    ${body}
    <script>
      (function () {
        function broken(im) {
          var t = im.parentNode; if (!t) return;
          t.classList.add('empty'); t.replaceChild(document.createTextNode('Image unavailable'), im);
        }
        function imagesReady() {
          return Promise.all(Array.prototype.slice.call(document.images).map(function (im) {
            if (im.complete) { if (!im.naturalWidth) broken(im); return Promise.resolve(); }
            return new Promise(function (r) {
              im.addEventListener('load', r);
              im.addEventListener('error', function () { broken(im); r(); });
            });
          }));
        }
        function timeout(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
        // paged.js does not repeat <thead> when a table runs onto the next page.
        // The continued part reserves room for it during layout (CSS margin on
        // table[data-split-from]); after the page is laid out the header fills that room.
        if (window.Paged && window.Paged.Handler) {
          class RepeatTableHeader extends window.Paged.Handler {
            afterPageLayout(pageElement) {
              pageElement.querySelectorAll('table.items[data-split-from]').forEach(function (t) {
                if (t.querySelector('thead') || !window.__poThead) return;
                t.insertBefore(window.__poThead.cloneNode(true), t.firstChild);
                t.classList.add('has-head');
              });
            }
          }
          window.Paged.registerHandlers(RepeatTableHeader);
        }
        window.addEventListener('load', function () {
          var th = document.querySelector('table.items thead');
          if (th) window.__poThead = th.cloneNode(true);
          var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
          Promise.race([Promise.all([imagesReady(), fonts]), timeout(12000)]).then(function () {
            if (!window.PagedPolyfill) { window.print(); return; }   // offline fallback: plain print
            return window.PagedPolyfill.preview().then(function () {
              setTimeout(function () { window.print(); }, 400);
            });
          }).catch(function () { window.print(); });
        });
      })();
    <\/script>
  </body></html>`;
}
